import React, { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, setDoc, updateDoc, query, where, deleteDoc } from "firebase/firestore";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import { db, firebaseConfig } from "../firebaseConfig";
import "../css/chapterDashboard.css";
import { getFunctions, httpsCallable } from "firebase/functions";

// Roles
const roles = ["viewer", "writer", "deacon", "publisher"];
const schoolRoles = ["student leader", "student member"];

// UsersManager component
export function UsersManager({
    embedded = false,
    allowedRoles = roles,
    title = "Users",
}) {
    const [currentRole, setCurrentRole] = useState(null);
    const [currentUid, setCurrentUid] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addError, setAddError] = useState("");
    const [removeError, setRemoveError] = useState("");
    const [addForm, setAddForm] = useState({
        email: "",
        name: "",
        role: allowedRoles[0] || "",
    });
    const [adding, setAdding] = useState(false);
    const [roleSaving, setRoleSaving] = useState({});
    const [removing, setRemoving] = useState({});

    // Watch auth state for current user/role
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUid(user.uid);
                const userRef = doc(db, "users", user.uid);
                const { getDoc } = await import("firebase/firestore");
                const snap = await getDoc(userRef);
                setCurrentRole(snap.exists() ? snap.data().role : null);
            } else {
                setCurrentRole(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Load users from Firestore (support both schemas)
    useEffect(() => {
        let mounted = true;
        async function loadUsers() {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, "users"));
                const nationalMode = isNationalRolesList(allowedRoles);

                let list = snap.docs
                    .map((d) => {
                        const data = d.data() || {};
                        return {
                            id: d.id,
                            _raw: data, // temporary for filtering
                            email: (data.email || d.id || "").trim(),
                            displayName: data.displayName || data.name || "",
                            role: data.role || (allowedRoles[0] || ""),
                        };
                    })
                    .filter((u) => {
                        const role = (u.role || "").toString().trim();

                        // Only show roles this manager is responsible for
                        if (!allowedRoles.includes(role)) return false;

                        // In national mode, exclude chapter/school-affiliated users
                        if (nationalMode && hasChapterAffiliation(u._raw)) return false;

                        return true;
                    })
                    .map(({ _raw, ...rest }) => rest);
                // Sort by displayName then email
                list.sort((a, b) => {
                    const nameA = (a.displayName || "").toLowerCase();
                    const nameB = (b.displayName || "").toLowerCase();
                    if (nameA < nameB) return -1;
                    if (nameA > nameB) return 1;
                    return (a.email || "").toLowerCase().localeCompare((b.email || "").toLowerCase());
                });
                if (mounted) setUsers(list);
            } catch (e) {
                if (mounted) setUsers([]);
            }
            if (mounted) setLoading(false);
        }
        loadUsers();
        // eslint-disable-next-line
    }, []);

    // Helper: normalize email
    function normalizeEmail(email) {
        return (email || "").trim().toLowerCase();
    }
    function isObjectWithKeys(v) {
        return !!v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length > 0;
    }

    function hasChapterAffiliation(data) {
        const invitedChapters = data?.invitedChapters || data?.chapters || null;
        const chapterAffiliated =
            !!invitedChapters &&
            ((Array.isArray(invitedChapters) && invitedChapters.length > 0) ||
                isObjectWithKeys(invitedChapters));

        // defensive for other possible schemas
        const schoolAffiliated =
            !!data?.schoolId ||
            !!data?.school ||
            !!data?.chapterId ||
            !!data?.chapter ||
            (Array.isArray(data?.schools) && data.schools.length > 0) ||
            isObjectWithKeys(data?.schools) ||
            (Array.isArray(data?.schoolIds) && data.schoolIds.length > 0) ||
            (Array.isArray(data?.chapterIds) && data.chapterIds.length > 0);

        return chapterAffiliated || schoolAffiliated;
    }

    function isNationalRolesList(allowed) {
        const allowedList = Array.isArray(allowed) ? allowed : [];
        // UsersManager is “national” when allowedRoles is a subset of national roles
        return allowedList.length > 0 && allowedList.every((r) => roles.includes(r));
    }
    // Add member handler
    async function handleAddMember(e) {
        e.preventDefault();
        setAddError("");
        setRemoveError("");
        setAdding(true);
        const email = normalizeEmail(addForm.email);
        const name = addForm.name ? addForm.name.trim() : "";
        const role = addForm.role;
        if (!email || !role) {
            setAddError("Email and role are required.");
            setAdding(false);
            return;
        }
        try {
            // Query if user already exists in Firestore
            const q = query(collection(db, "users"), where("email", "==", email));
            const snap = await getDocs(q);

            // Ensure Auth user exists (or create) via Cloud Function.
            // This function should create the Auth user if missing and return { uid } (or { uid, email }).
            const functions = getFunctions();
            const createUserAccount = httpsCallable(functions, "createUserAccount");

            let existingDocId = null;
            let existingData = null;
            if (!snap.empty) {
                existingDocId = snap.docs[0].id;
                existingData = snap.docs[0].data() || null;
            }

            let uid = null;
            try {
                const res = await createUserAccount({
                    email,
                    displayName: name || "",
                    name: name || "",
                });
                uid = res?.data?.uid || res?.data?.user?.uid || null;
            } catch (err) {
                console.error("createUserAccount failed:", err);
                const msg =
                    err?.message ||
                    err?.details ||
                    "Could not create/ensure Auth user via Cloud Function createUserAccount.";
                throw new Error(msg);
            }

            if (!uid) {
                throw new Error("Cloud Function createUserAccount did not return a uid.");
            }

            // Upsert Firestore user doc under the UID
            const uidRef = doc(db, "users", uid);

            // If there was an existing Firestore doc that used the email as the id (or some other id),
            // migrate it into the UID doc and delete the old doc to avoid duplicates.
            if (existingDocId && existingDocId !== uid) {
                // Merge old data (if any) into the new UID doc, then delete the old doc.
                await setDoc(
                    uidRef,
                    {
                        ...(existingData || {}),
                        email,
                        role,
                        displayName: name || existingData?.displayName || existingData?.name || "",
                        name: name || existingData?.name || existingData?.displayName || "",
                        updatedAt: new Date().toISOString(),
                        authUid: uid,
                        needsAuthCreation: false,
                    },
                    { merge: true }
                );

                // Best-effort cleanup of the old doc (email-id stub or legacy id)
                try {
                    await deleteDoc(doc(db, "users", existingDocId));
                } catch (cleanupErr) {
                    console.warn("Could not delete legacy user doc:", cleanupErr);
                }
            } else if (existingDocId === uid) {
                // Update existing UID doc
                await setDoc(
                    uidRef,
                    {
                        email,
                        role,
                        displayName: name || existingData?.displayName || existingData?.name || "",
                        name: name || existingData?.name || existingData?.displayName || "",
                        updatedAt: new Date().toISOString(),
                        authUid: uid,
                        needsAuthCreation: false,
                    },
                    { merge: true }
                );
            } else {
                // Create new UID doc
                await setDoc(
                    uidRef,
                    {
                        email,
                        role,
                        displayName: name || "",
                        name: name || "",
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        authUid: uid,
                        needsAuthCreation: false,
                    },
                    { merge: true }
                );
            }

            // Update local state (remove any legacy entry that matched by email or old id, then add/update UID entry)
            setUsers((prev) => {
                const nextUser = {
                    id: uid,
                    email,
                    displayName: name || "",
                    role,
                };

                const filtered = prev.filter(
                    (u) => u.id !== uid && normalizeEmail(u.email) !== email
                );

                return [...filtered, nextUser].sort((a, b) => {
                    const nameA = (a.displayName || "").toLowerCase();
                    const nameB = (b.displayName || "").toLowerCase();
                    if (nameA < nameB) return -1;
                    if (nameA > nameB) return 1;
                    return (a.email || "").toLowerCase().localeCompare((b.email || "").toLowerCase());
                });
            });

            setAddForm({ email: "", name: "", role: allowedRoles[0] || "" });
        } catch (e) {
            setAddError(e?.message || "Failed to add member (Auth and/or database).");
        }
        setAdding(false);
    }

    // Inline role edit handler
    async function handleRoleChange(id, newRole) {
        setRoleSaving((s) => ({ ...s, [id]: true }));
        try {
            const userRef = doc(db, "users", id);
            await updateDoc(userRef, { role: newRole, updatedAt: new Date().toISOString() });
            setUsers((prev) =>
                prev.map((u) =>
                    u.id === id ? { ...u, role: newRole } : u
                )
            );
        } catch (e) {
            // Optionally show error
        }
        setRoleSaving((s) => ({ ...s, [id]: false }));
    }

    // Remove handler
    async function handleRemove(id) {
        setRemoveError("");
        setRemoving((r) => ({ ...r, [id]: true }));

        try {
            // Always attempt to remove the Firestore document.
            // If the user is a real Auth user (UID doc), we ALSO try to delete from Auth via Cloud Function.
            if (!id.includes("@")) {
                try {
                    const functions = getFunctions();
                    const fn = httpsCallable(functions, "deleteUserAccount");
                    await fn({ uid: id });
                } catch (e) {
                    console.error("deleteUserAccount failed:", e);
                    // Still proceed to remove Firestore doc so UI/admin state stays consistent.
                    setRemoveError(
                        (e && (e.message || e.details))
                            ? `Could not delete Auth user (Cloud Function error). The user record was removed from the database, but the Auth account may still exist. Details: ${e.message || e.details}`
                            : "Could not delete Auth user (Cloud Function error). The user record was removed from the database, but the Auth account may still exist."
                    );
                }
            }

            // Remove the Firestore doc for BOTH schemas (email-id stubs and UID docs)
            await deleteDoc(doc(db, "users", id));

            setUsers((prev) => prev.filter((u) => u.id !== id));
        } catch (e) {
            console.error("handleRemove failed:", e);
            setRemoveError(e?.message || "Failed to remove user.");
        } finally {
            setRemoving((r) => ({ ...r, [id]: false }));
        }
    }

    // Layout
    const canEdit = currentRole === "publisher";
    const showActions = canEdit;
    const showRoleDropdown = canEdit;

    // Outer container classes
    // NOTE: chapterDashboard.css scopes most dashboard styles under `.cd-shell ...`
    // so we add `cd-shell` even for embedded usage so the same classes render correctly.
    const outerClasses = embedded
        ? "cd-shell"
        : "cd-shell subscribe-page users-page";
    const heroClasses = embedded
        ? ""
        : "subscribe-hero";

    // Helper text
    const helperText =
        "Invite others to your organization. Add members by email and assign a role.";
    const panelTitle = title || "Users";
    const panelSubtitle = helperText;

    return (
        <div className={outerClasses}>
            <div className={heroClasses} style={embedded ? {} : { padding: "1.5rem 2rem" }}>
                {/* Match Chapter Dashboard structure/classes */}
                <div className="nd-card" style={embedded ? { marginTop: 12 } : {}}>
                    <div className="nd-panelHeader">
                        <div>
                            <div className="nd-panelTitle">{panelTitle}</div>
                            <div className="nd-subtitle" style={{ marginTop: 6 }}>{panelSubtitle}</div>
                        </div>
                        <div className="nd-muted" style={{ fontWeight: 800 }}>
                            {loading ? "" : `${users.length} member${users.length === 1 ? "" : "s"}`}
                        </div>
                    </div>

                    <div className="nd-panelBody">
                        {canEdit && (
                            <div className="nd-card" style={{ marginBottom: 16 }}>
                                <div className="nd-panelHeader" style={{ borderBottom: "1px solid rgba(15, 23, 42, 0.06)" }}>
                                    <div className="nd-panelTitle" style={{ fontSize: 15 }}>Add member</div>
                                </div>
                                <div className="nd-panelBody">
                                    <form onSubmit={handleAddMember} autoComplete="off">
                                        <div className="nd-formGrid">
                                            <div className="nd-field">
                                                <label className="nd-fieldLabel">Email</label>
                                                <input
                                                    className="nd-input"
                                                    type="email"
                                                    required
                                                    value={addForm.email}
                                                    onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                                                    placeholder="name@school.edu"
                                                />
                                            </div>

                                            <div className="nd-field">
                                                <label className="nd-fieldLabel">Name (optional)</label>
                                                <input
                                                    className="nd-input"
                                                    type="text"
                                                    value={addForm.name}
                                                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                                                    placeholder="Full name"
                                                />
                                            </div>

                                            <div className="nd-field">
                                                <label className="nd-fieldLabel">Role</label>
                                                <select
                                                    className="nd-select"
                                                    required
                                                    value={addForm.role}
                                                    onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
                                                >
                                                    {allowedRoles.map((r) => (
                                                        <option key={r} value={r}>
                                                            {r}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="nd-field" style={{ justifyContent: "flex-end" }}>
                                                <label className="nd-fieldLabel" style={{ opacity: 0 }}>Action</label>
                                                <button
                                                    className="nd-btnPrimary"
                                                    type="submit"
                                                    disabled={adding}
                                                    style={{ width: "100%" }}
                                                >
                                                    {adding ? "Adding…" : "Add"}
                                                </button>
                                            </div>
                                        </div>

                                        {addError && (
                                            <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700, fontSize: 13 }}>
                                                {addError}
                                            </div>
                                        )}
                                    </form>
                                </div>
                            </div>
                        )}

                        <div className="nd-tableWrap">
                            {removeError && (
                                <div style={{ padding: 14, color: "#b91c1c", fontWeight: 800, fontSize: 13 }}>
                                    {removeError}
                                </div>
                            )}
                            {loading ? (
                                <div style={{ padding: 14, fontWeight: 700, color: "rgba(15, 23, 42, 0.65)" }}>Loading…</div>
                            ) : (
                                <table className="nd-table">
                                    <thead>
                                        <tr>
                                            <th>NAME</th>
                                            <th>EMAIL</th>
                                            <th>ROLE</th>
                                            {showActions && <th>ACTIONS</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length === 0 ? (
                                            <tr>
                                                <td colSpan={showActions ? 4 : 3} style={{ padding: 14, color: "rgba(15, 23, 42, 0.65)", fontWeight: 700 }}>
                                                    No members found.
                                                </td>
                                            </tr>
                                        ) : (
                                            users.map((u) => (
                                                <tr key={u.id}>
                                                    <td style={{ fontWeight: 800 }}>
                                                        {u.displayName || <span style={{ color: "rgba(15, 23, 42, 0.35)", fontWeight: 900 }}>—</span>}
                                                    </td>
                                                    <td style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace", fontSize: 13 }}>
                                                        {u.email}
                                                    </td>
                                                    <td>
                                                        {showRoleDropdown ? (
                                                            <select
                                                                value={u.role}
                                                                onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                                                disabled={roleSaving[u.id]}
                                                                className="nd-select"
                                                                style={{ maxWidth: 220 }}
                                                            >
                                                                {allowedRoles.map((r) => (
                                                                    <option key={r} value={r}>
                                                                        {r}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span className="title-case">{u.role}</span>
                                                        )}
                                                    </td>
                                                    {showActions && (
                                                        <td>
                                                            {u.id !== currentUid ? (
                                                                <button
                                                                    type="button"
                                                                    className="nd-btn"
                                                                    onClick={() => handleRemove(u.id)}
                                                                    disabled={removing[u.id]}
                                                                >
                                                                    {removing[u.id] ? "Removing…" : "Remove"}
                                                                </button>
                                                            ) : (
                                                                <span style={{ color: "rgba(15, 23, 42, 0.35)", fontWeight: 900 }}>—</span>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default function UsersPage() {
    // Standalone users page
    return (
        <>
            <Navbar />
            <UsersManager
                embedded={false}
                title="Users & Roles"
                allowedRoles={roles}
            />
            <Footer />
        </>
    );
}
export function NationalTeamWidget() {
    const [members, setMembers] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        let mounted = true;

        async function load() {
            setLoading(true);
            setError("");

            try {
                // Fetch all users.
                const snap = await getDocs(collection(db, "users"));
                const list = [];

                snap.forEach((docSnap) => {
                    const d = docSnap.data() || {};
                    const invitedChapters = d.invitedChapters || d.chapters || null;
                    const hasChapterAffiliation =
                        !!invitedChapters &&
                        ((Array.isArray(invitedChapters) && invitedChapters.length > 0) ||
                            (!Array.isArray(invitedChapters) && typeof invitedChapters === "object" && Object.keys(invitedChapters).length > 0));

                    const role = (d.role || "").toString().trim();
                    const isNationalRole = roles.includes(role);

                    const name = (d.name || d.displayName || "").toString().trim();
                    if (!name) return;

                    // National team = users with a national-level role and no chapter/school affiliations.
                    if (!isNationalRole) return;
                    if (hasChapterAffiliation) return;

                    list.push({ id: docSnap.id, name, role });
                });

                list.sort((a, b) => {
                    const ar = (a.role || "").toLowerCase();
                    const br = (b.role || "").toLowerCase();
                    if (ar < br) return -1;
                    if (ar > br) return 1;
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                });

                if (!mounted) return;
                setMembers(list);
            } catch (e) {
                console.error("NationalTeamWidget load error:", e);
                if (!mounted) return;
                setError("Could not load national team.");
                setMembers([]);
            } finally {
                if (mounted) setLoading(false);
            }
        }

        load();
        return () => {
            mounted = false;
        };
    }, []);

    if (loading) {
        return (
            <div className="national-team-widget">
                <div className="national-team-widget__loading">Loading…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="national-team-widget">
                <div className="national-team-widget__empty">{error}</div>
            </div>
        );
    }

    if (!members.length) {
        return (
            <div className="national-team-widget">
                <div className="national-team-widget__empty">No team members found.</div>
            </div>
        );
    }

    return (
        <div className="national-team-widget" aria-label="National team members">
            <div className="national-team-widget__list">
                {members.map((m) => (
                    <div key={m.id} className="national-team-widget__row">
                        <div className="national-team-widget__name">{m.name}</div>
                        <div className="national-team-widget__role title-case">{m.role || "Member"}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}